import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Quotes as Quote } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SiteFooter } from "../components/SiteFooter";

const API = process.env.REACT_APP_BACKEND_URL;
const C = { bg: "#FAF7F0", ink: "#2A1E5C", ink2: "#5D5A6B", muted: "#8B7E6A", gold: "#F5CB5C", red: "#DE3C4B", line: "#E8E2D5", card: "#FFFFFF" };

export default function QuoteApprovalPage() {
  const [params] = useSearchParams();
  const token = (params.get("token") || "").trim();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [quote, setQuote] = useState("");
  const [status, setStatus] = useState("pending");
  const [credit, setCredit] = useState("");
  const [mode, setMode] = useState("first_name");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [initials, setInitials] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setNotFound(true); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/testimonials/approval?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (!res.ok) { setNotFound(true); return; }
        const data = await res.json();
        setQuote(data.quote);
        setStatus(data.status);
        setCredit(data.display_credit || "");
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const respond = async (decision) => {
    if (decision === "approve") {
      if (mode === "first_name" && !name.trim()) { toast.error("Add your first name so we can credit you"); return; }
      if (mode === "initials" && !initials.trim()) { toast.error("Add your initials so we can credit you"); return; }
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/testimonials/approval`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token, decision,
          attribution_type: decision === "approve" ? mode : undefined,
          attribution_name: name.trim() || undefined,
          attribution_role: role.trim() || undefined,
          attribution_initials: initials.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong");
      setStatus(data.status);
      setCredit(data.display_credit || "");
    } catch (e) {
      toast.error(e.message || "Something went wrong, try again");
    } finally {
      setBusy(false);
    }
  };

  const shell = (children) => (
    <div data-testid="quote-approval-page" style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px" }}>
        <div style={{ maxWidth: 520, width: "100%" }}>{children}</div>
      </div>
      <SiteFooter />
    </div>
  );

  if (loading) return shell(<p style={{ fontFamily: "Arial", color: C.muted, textAlign: "center" }}>Loading…</p>);

  if (notFound) return shell(
    <div data-testid="quote-not-found" style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: 32, textAlign: "center" }}>
      <h1 style={{ fontFamily: "Georgia, serif", color: C.ink, fontSize: 24, margin: "0 0 8px" }}>Link not found</h1>
      <p style={{ fontFamily: "Arial", color: C.ink2, fontSize: 15, lineHeight: 1.6 }}>
        This approval link doesn't look right. Check the link in your email, or just reply to it and we'll sort it out.
      </p>
      <Link to="/" style={{ fontFamily: "Arial", color: C.red, fontWeight: "bold", fontSize: 14 }}>← Back to Payday Relay</Link>
    </div>
  );

  if (status === "approved") return shell(
    <div data-testid="approval-success" style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: 32, textAlign: "center" }}>
      <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.gold, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <Check size={28} color={C.ink} />
      </div>
      <h1 style={{ fontFamily: "Georgia, serif", color: C.ink, fontSize: 24, margin: "0 0 8px" }}>Thank you! 🏁</h1>
      <p style={{ fontFamily: "Arial", color: C.ink2, fontSize: 15, lineHeight: 1.6 }}>
        Your quote is approved{credit ? <> and will be credited to <b>{credit}</b></> : ""}. It means a lot, real words from real racers are what launch this thing.
      </p>
      <Link to="/" style={{ fontFamily: "Arial", color: C.red, fontWeight: "bold", fontSize: 14 }}>← Back to Payday Relay</Link>
    </div>
  );

  if (status === "declined") return shell(
    <div data-testid="approval-declined" style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: 32, textAlign: "center" }}>
      <h1 style={{ fontFamily: "Georgia, serif", color: C.ink, fontSize: 24, margin: "0 0 8px" }}>No problem at all.</h1>
      <p style={{ fontFamily: "Arial", color: C.ink2, fontSize: 15, lineHeight: 1.6 }}>
        We won't use your quote, zero hard feelings. Thanks for being part of the beta.
      </p>
      <Link to="/" style={{ fontFamily: "Arial", color: C.red, fontWeight: "bold", fontSize: 14 }}>← Back to Payday Relay</Link>
    </div>
  );

  const optionStyle = (active) => ({
    display: "block", cursor: "pointer", border: `2px solid ${active ? C.red : C.line}`,
    borderRadius: 12, padding: "14px 16px", marginBottom: 10, background: active ? "#FDF3F4" : C.card,
    transition: "border-color 0.15s ease, background-color 0.15s ease",
  });
  const inputStyle = {
    width: "100%", boxSizing: "border-box", fontFamily: "Arial", fontSize: 15,
    padding: "10px 12px", border: `1px solid ${C.line}`, borderRadius: 8, marginTop: 8,
  };

  return shell(
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "28px 28px 24px" }}>
      <p style={{ fontFamily: "Arial", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: C.muted, margin: "0 0 6px" }}>Payday Relay · Beta</p>
      <h1 style={{ fontFamily: "Georgia, serif", color: C.ink, fontSize: 26, margin: "0 0 16px" }}>May we quote you?</h1>

      <div style={{ background: C.bg, borderLeft: `4px solid ${C.gold}`, borderRadius: 8, padding: "16px 18px", marginBottom: 20 }}>
        <Quote size={18} color={C.muted} style={{ marginBottom: 6 }} />
        <p data-testid="quote-text" style={{ fontFamily: "Georgia, serif", fontStyle: "italic", color: C.ink, fontSize: 17, lineHeight: 1.55, margin: 0 }}>
          “{quote}”
        </p>
      </div>

      <p style={{ fontFamily: "Arial", color: C.ink2, fontSize: 14, lineHeight: 1.6, margin: "0 0 12px" }}>
        We'd love to use this on the launch page. You choose exactly how you're credited:
      </p>

      <label data-testid="credit-first-name" style={optionStyle(mode === "first_name")}>
        <input type="radio" name="credit" checked={mode === "first_name"} onChange={() => setMode("first_name")} style={{ marginRight: 8 }} />
        <span style={{ fontFamily: "Arial", fontWeight: "bold", color: C.ink, fontSize: 14 }}>First name + what you do</span>
        <span style={{ fontFamily: "Arial", color: C.muted, fontSize: 13 }}>, e.g. “Sarah, remote designer”</span>
        {mode === "first_name" && (
          <div>
            <input data-testid="credit-name-input" style={inputStyle} placeholder="First name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
            <input data-testid="credit-role-input" style={inputStyle} placeholder="What you do (optional), e.g. remote designer" value={role} onChange={(e) => setRole(e.target.value)} maxLength={80} />
          </div>
        )}
      </label>

      <label data-testid="credit-initials" style={optionStyle(mode === "initials")}>
        <input type="radio" name="credit" checked={mode === "initials"} onChange={() => setMode("initials")} style={{ marginRight: 8 }} />
        <span style={{ fontFamily: "Arial", fontWeight: "bold", color: C.ink, fontSize: 14 }}>Initials only</span>
        <span style={{ fontFamily: "Arial", color: C.muted, fontSize: 13 }}>, e.g. “S.K.”</span>
        {mode === "initials" && (
          <input data-testid="credit-initials-input" style={inputStyle} placeholder="Your initials, e.g. S.K." value={initials} onChange={(e) => setInitials(e.target.value)} maxLength={12} />
        )}
      </label>

      <button
        data-testid="approve-quote-btn"
        onClick={() => respond("approve")}
        disabled={busy}
        style={{
          width: "100%", marginTop: 8, background: C.red, color: "#fff", fontFamily: "Arial",
          fontWeight: "bold", fontSize: 15, border: "none", borderRadius: 999, padding: "14px 0",
          cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1,
        }}
      >
        {busy ? "Saving…" : "Yes, you may quote me 🏁"}
      </button>

      <button
        data-testid="decline-quote-btn"
        onClick={() => respond("decline")}
        disabled={busy}
        style={{
          width: "100%", marginTop: 10, background: "transparent", color: C.muted, fontFamily: "Arial",
          fontSize: 13, border: "none", cursor: busy ? "default" : "pointer", textDecoration: "underline",
        }}
      >
        Please don't use my quote
      </button>
    </div>
  );
}
